const MaxHeliusImageBytes = 100_000;

/**
 * Check if an external image URL is allowed to be loaded.
 *
 * We currently allow images from external sources which we don't control.
 * Those images could be very large. In particular, we have issues with Helius,
 * which we use for Solana asset data, which can contain images of 2-3mb. This
 * is very slow to render in the Dashboard etc.
 *
 * For Helius images, this checks the content-length before rendering/loading.
 * If the image is too large, we skip it.
 *
 * The better solution would be to resize, compress and cache the image, but we
 * don't have a framework for that yet.
 */
export async function isAllowedExternalImageUrl(src: string): Promise<boolean> {
  if (!isHeliusImageUrl(src)) {
    return true;
  }

  return isAllowedHeliusImageUrl(src);
}

export function isHeliusImageUrl(src: string): boolean {
  try {
    const url = new URL(src);

    return url.hostname === "cdn.helius-rpc.com";
  } catch {
    return false;
  }
}

async function isAllowedHeliusImageUrl(src: string): Promise<boolean> {
  try {
    const response = await fetch(src, {
      method: "HEAD",
    });

    if (!response.ok) {
      return false;
    }

    const contentLength = response.headers.get("content-length");

    if (!contentLength) {
      return false;
    }

    const bytes = Number.parseInt(contentLength, 10);

    const contentLengthBytes =
      Number.isFinite(bytes) && bytes >= 0 ? bytes : undefined;

    if (!contentLengthBytes) {
      return false;
    }

    return contentLengthBytes <= MaxHeliusImageBytes;
  } catch {
    // If the size check fails, block the image.
    return false;
  }
}
